/**
 * assetReportHelper.js — data loading + generic Excel/print for Fixed Assets reports.
 */
import { collection, getDocs, query, where } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../config/firebase';
import { loadFixedAssets, loadFixedAssetCategories } from './fixedAssetHelper';
import { appreciationOf, costBasisValueOf } from './depreciationHelper';
import { addPrintBrand } from './printBranding';

/**
 * Load everything the asset reports need, branch-filtered.
 * @returns {{ assets, categories, depRuns, disposals }}
 */
export const loadAssetReportData = async (companyId, activeBranch) => {
  const [assets, categories, depSnap, fadSnap] = await Promise.all([
    loadFixedAssets(companyId),
    loadFixedAssetCategories(companyId),
    getDocs(query(collection(db, 'companies', companyId, 'transactions'), where('tranTypeCode', '==', 'DEP'))),
    getDocs(query(collection(db, 'companies', companyId, 'transactions'), where('tranTypeCode', '==', 'FAD'))),
  ]);

  const branchId = activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null;
  const inBranch = (x) => !branchId || x.branchId === branchId || x.branchId == null;

  const depRuns = depSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.status !== 'Cancelled' && inBranch(t));
  const disposals = fadSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.status !== 'Cancelled' && inBranch(t));

  return {
    assets: assets.filter(inBranch),
    categories,
    depRuns,
    disposals,
  };
};

/** Export an array of plain objects to an .xlsx file. */
export const exportRowsToExcel = (filename, sheetName, rows) => {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{}]);
  XLSX.utils.book_append_sheet(wb, ws, (sheetName || 'Sheet1').slice(0, 31));
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const inRange = (date, start, end) => {
  if (!date) return false;
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
};

/**
 * Per-category movement for a period (FY). Reconciles the Balance Sheet asset note:
 *   Opening NBV + Additions + Appreciation − Depreciation − DisposalNBV = Closing NBV.
 *
 * @param {object[]} assets    - all fixedAssets (incl. Disposed)
 * @param {object[]} depRuns   - DEP transactions (have docDate + depreciationSnapshot[])
 * @param {string} fyStart 'YYYY-MM-DD' (or null for all-time)
 * @param {string} fyEnd   'YYYY-MM-DD' (or null)
 * @param {object} [opts] - { costBasis: true } leaves Asset Appreciation (and the
 *   depreciation charged on it) out — used by the FBR statement, which stays on cost.
 * @returns {{ rows: object[], totals: object }}
 */
export const computeFixedAssetMovement = (assets, depRuns, fyStart, fyEnd, opts = {}) => {
  const costBasis = opts.costBasis === true;
  const assetMap = {};
  assets.forEach(a => { assetMap[a.id] = a; });
  const cats = {};
  const ensure = (id, name) => {
    const key = id || 'uncat';
    if (!cats[key]) cats[key] = { categoryId: id || null, categoryName: name || 'Uncategorised', additions: 0, appreciation: 0, depreciation: 0, disposalNBV: 0, closingNBV: 0, openingNBV: 0 };
    return cats[key];
  };

  // Additions, appreciation, disposals, closing NBV from the asset register.
  assets.forEach(a => {
    const c = ensure(a.categoryId, a.categoryName);
    const appr = appreciationOf(a);
    if (inRange(a.acquisitionDate, fyStart, fyEnd)) c.additions = r2(c.additions + (Number(a.acquisitionCost) || 0));
    if (appr > 0 && !costBasis) {
      (a.appreciationEntries || []).forEach(e => {
        if (inRange(e.date, fyStart, fyEnd)) c.appreciation = r2(c.appreciation + (Number(e.amount) || 0));
      });
    }
    if (a.status === 'Disposed') {
      if (inRange(a.disposalDate, fyStart, fyEnd)) {
        const nbvAtDisposal = appr > 0 && costBasis
          ? costBasisValueOf(a)
          : r2((Number(a.acquisitionCost) || 0) + appr - (Number(a.accumulatedDepreciation) || 0));
        c.disposalNBV = r2(c.disposalNBV + nbvAtDisposal);
      }
    } else {
      c.closingNBV = r2(c.closingNBV + (appr > 0 && costBasis ? costBasisValueOf(a) : (Number(a.netBookValue) || 0)));
    }
  });

  // Depreciation charged in the period, mapped per category via the run snapshot.
  depRuns.forEach(run => {
    if (!inRange(run.docDate, fyStart, fyEnd)) return;
    (run.depreciationSnapshot || []).forEach(line => {
      const a = assetMap[line.assetId];
      const c = ensure(a?.categoryId, a?.categoryName);
      const onAppreciation = costBasis ? (Number(line.appreciationPart) || 0) : 0;
      c.depreciation = r2(c.depreciation + (Number(line.amount) || 0) - onAppreciation);
    });
  });

  // Opening NBV back-calculated from the period flows.
  Object.values(cats).forEach(c => {
    c.openingNBV = r2(c.closingNBV + c.depreciation + c.disposalNBV - c.additions - c.appreciation);
  });

  const rows = Object.values(cats).filter(c =>
    c.additions || c.appreciation || c.depreciation || c.disposalNBV || c.closingNBV || c.openingNBV);
  const totals = rows.reduce((t, c) => ({
    openingNBV: r2(t.openingNBV + c.openingNBV), additions: r2(t.additions + c.additions),
    appreciation: r2(t.appreciation + c.appreciation),
    depreciation: r2(t.depreciation + c.depreciation), disposalNBV: r2(t.disposalNBV + c.disposalNBV),
    closingNBV: r2(t.closingNBV + c.closingNBV),
  }), { openingNBV: 0, additions: 0, appreciation: 0, depreciation: 0, disposalNBV: 0, closingNBV: 0 });

  return { rows: rows.sort((a, b) => a.categoryName.localeCompare(b.categoryName)), totals };
};

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * Generic print for a tabular report.
 * @param {object} cfg - {
 *   company, branch, title, filters: string,
 *   kpis: [{label, value, highlight}],
 *   columns: [{key, label, r}],          // r = right-align (numeric)
 *   rows: [{ ...cell values }],
 *   totals: { [key]: value } | null,      // optional footer row
 *   footerNote: string
 * }
 */
export const printAssetReport = (cfg) => {
  const { company = {}, branch = null, title, filters = '', kpis = [], columns = [], rows = [], totals = null, footerNote = '' } = cfg;
  const accent = '#800020';

  const head = columns.map(c => `<th class="${c.r ? 'r' : ''}">${esc(c.label)}</th>`).join('');
  const body = rows.length === 0
    ? `<tr><td colspan="${columns.length}" style="text-align:center;color:#888;padding:24px">No data for the current filters.</td></tr>`
    : rows.map(row => `<tr>${columns.map(c => `<td class="${c.r ? 'r' : ''}">${esc(row[c.key])}</td>`).join('')}</tr>`).join('');
  const foot = totals
    ? `<tfoot><tr>${columns.map((c, i) => `<td class="${c.r ? 'r' : ''}">${i === 0 ? 'Total' : esc(totals[c.key] ?? '')}</td>`).join('')}</tr></tfoot>`
    : '';
  const kpiHtml = kpis.length === 0 ? '' : `<div class="kpis">${kpis.map(k => `
    <div class="kpi ${k.highlight ? 'hl' : ''}"><div class="kl">${esc(k.label)}</div><div class="kv">${esc(k.value)}</div></div>`).join('')}</div>`;

  const html = `<!DOCTYPE html><html><head><title>${esc(title)}</title><style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Segoe UI',sans-serif;color:#1A1A1A;padding:24px;line-height:1.4}
    @page{size:A4 landscape;margin:12mm}@media print{body{padding:0}}
    .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid ${accent};padding-bottom:12px;margin-bottom:16px}
    .company-name{font-size:18px;font-weight:700;color:${accent}}
    .meta{font-size:11px;color:#666;margin-top:2px}
    .doc-title{color:${accent};font-size:20px;font-weight:700;text-align:right}
    .filters{font-size:12px;color:#444;margin-bottom:14px;padding:8px 12px;background:#f7f7f7;border-left:3px solid ${accent};border-radius:3px}
    .kpis{display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap}
    .kpi{flex:1;min-width:140px;padding:8px 10px;border:1px solid #ddd;border-radius:4px;text-align:center;background:#fff}
    .kpi.hl{background:${accent};color:#fff;border-color:${accent}}
    .kl{font-size:9px;color:#666;text-transform:uppercase;letter-spacing:0.4px;font-weight:600}
    .kpi.hl .kl{color:rgba(255,255,255,0.85)}
    .kv{font-size:14px;font-weight:700;font-family:monospace;margin-top:3px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th,td{padding:6px 8px;border-bottom:1px solid #e5e5e5;text-align:left}
    th{background:#f5f5f5;color:#555;font-weight:700;text-transform:uppercase;font-size:9px;letter-spacing:0.3px}
    td.r,th.r{text-align:right;font-family:monospace}
    tfoot td{font-weight:700;border-top:2px solid #999;background:#f5f5f5}
    .footer{margin-top:18px;font-size:10px;color:#888;text-align:center;border-top:1px solid #eee;padding-top:8px}
  </style></head><body>
    <div class="header">
      <div>
        <div class="company-name">${esc(company.companyName || 'AMAL ERP')}</div>
        ${branch ? `<div class="meta">Branch: ${esc(branch.branchName)}</div>` : ''}
        <div class="meta">Printed ${new Date().toLocaleString('en-PK')}</div>
      </div>
      <div class="doc-title">${esc(title)}</div>
    </div>
    ${filters ? `<div class="filters">${filters}</div>` : ''}
    ${kpiHtml}
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${foot}</table>
    <div class="footer">${esc(footerNote || title)} · ${rows.length} row${rows.length !== 1 ? 's' : ''} · ${esc(company.companyName || 'AMAL ERP')}</div>
  </body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('Please allow pop-ups to print this report.'); return; }
  w.document.write(html);
  addPrintBrand(w);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 150);
};
